// src/features/records/components/shared/AttachmentThumb.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styles from './AttachmentThumb.module.css';
import { useThumbUrls } from '../../hooks/useThumbUrls';

/**
 * Unified thumbnail component for both images and videos
 * Handles responsive srcSet, error recovery, and video overlays
 */
const AttachmentThumb = ({ 
  attachment, 
  variant = 'compact', 
  className,
  priority = false 
}) => {
  const { t } = useTranslation();
  const isVideo = attachment.type === 'video';

  const { src, srcSet, loading, error, onImgErrorOnce } = useThumbUrls(attachment);

  // Responsible image hints. Tune to actual CSS grid breakpoints.
  const sizes = variant === 'grid' 
    ? '(min-width: 1200px) 220px, (min-width: 768px) 33vw, 50vw'
    : '64px'; // small preview in cards

  // Keep layout stable. If width/height known, honor actual aspect. Otherwise default to square.
  const aspectRatio = attachment.width && attachment.height
    ? `${attachment.width} / ${attachment.height}`
    : '1 / 1';

  // Accessible alt: favor filename; add a prefix for videos
  const baseName = attachment.metadata?.filename
    || attachment.name
    || t('records.detail.attachments.fallback_name', 'Attachment');
  const formattedDuration = typeof attachment.duration === 'number'
    ? formatDuration(attachment.duration, t)
    : '';
  const alt = isVideo
    ? (
      formattedDuration
        ? t('records.detail.attachments.video_alt_with_duration', {
            name: baseName,
            duration: formattedDuration,
            defaultValue: 'Video: {{name}} ({{duration}})'
          })
        : t('records.detail.attachments.video_alt', {
            name: baseName,
            defaultValue: 'Video: {{name}}'
          })
    )
    : baseName;

  // Determine what to render based on state
  const renderContent = () => {
    if (loading && !src) {
      return (
        <div 
          className={styles.placeholder}
          role="img"
          aria-label={t('records.detail.attachments.placeholder_loading', {
            name: alt,
            defaultValue: '{{name}} (loading)'
          })}
        />
      );
    }

    if (error && !src) {
      return (
        <div 
          className={styles.errorPlaceholder}
          role="img"
          aria-label={t('records.detail.attachments.placeholder_failed', {
            name: alt,
            defaultValue: '{{name}} (failed to load)'
          })}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </div>
      );
    }

    if (src) {
      return (
        <img
          src={src}
          srcSet={srcSet}
          sizes={srcSet ? sizes : undefined}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onError={onImgErrorOnce}
          className={styles.img}
        />
      );
    }

    // Fallback placeholder
    return (
      <div 
        className={styles.placeholder}
        role="img"
        aria-label={t('records.detail.attachments.placeholder_missing', {
          name: alt,
          defaultValue: '{{name}} (no thumbnail)'
        })}
      />
    );
  };

  return (
    <div 
      className={`${styles.thumb} ${className || ''} ${isVideo ? styles.video : ''}`}
      style={{ aspectRatio }}
    >
      {renderContent()}

      {isVideo && (
        <div className={styles.videoOverlay} aria-hidden="true">
          <div className={styles.playButton}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
          {typeof attachment.duration === 'number' && (
            <div className={styles.duration}>
              {formattedDuration}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Format video duration from seconds to human-readable format
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration (e.g., "1:23" or "45s")
 */
function formatDuration(seconds, t) {
  if (!Number.isFinite(seconds)) return '';
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0
    ? `${m}:${String(r).padStart(2, '0')}`
    : t('records.time.duration_seconds', { count: r, defaultValue: '{{count}}s' });
}

AttachmentThumb.propTypes = {
  attachment: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    storagePath: PropTypes.string,
    thumbnails: PropTypes.object,
    width: PropTypes.number,
    height: PropTypes.number,
    duration: PropTypes.number,
    name: PropTypes.string,
    metadata: PropTypes.shape({
      filename: PropTypes.string
    }),
    // Pre-signed URLs if provided by parent
    thumbnailUrl: PropTypes.string,
    posterUrl: PropTypes.string
  }).isRequired,
  variant: PropTypes.oneOf(['compact', 'grid']),
  className: PropTypes.string,
  priority: PropTypes.bool
};

export default AttachmentThumb;
